/**
 * Dashboard Actions for AI Agent
 * Creates and modifies dashboards with Swapy integration
 */
import { v4 as uuidv4 } from 'uuid';

export interface DashboardWidget {
  id?: string;
  type: string;
  metric_type: string;
  title: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  config: {
    time_range?: any;
    filters?: Record<string, any>;
  };
}

export interface CreateDashboardAction {
  type: 'create_dashboard';
  params: {
    title: string;
    description?: string;
    widgets: DashboardWidget[];
    layout_type: 'grid' | 'freeform';
    is_default?: boolean;
  };
}

export interface ModifyDashboardAction {
  type: 'modify_dashboard';
  params: {
    dashboard_id: string;
    modification: string;
    entities: any;
  };
}

export class DashboardActions {
  constructor(private dashboardService: any) {}

  /**
   * Create a new dashboard
   */
  async createDashboard(action: CreateDashboardAction): Promise<{
    success: boolean;
    error?: string;
    dashboardId?: string;
  }> {
    try {
      const { title, description, widgets, layout_type } = action.params;
      
      // Generate Swapy-compatible layout
      const swapyLayout = this.generateSwapyLayout(widgets);
      
      // Create dashboard via service
      const dashboard = await this.dashboardService.createDashboard({
        title,
        description,
        layout_type,
        config: {
          swapy_enabled: true,
          layout: swapyLayout,
        },
        widgets: widgets.map(w => ({
          ...w,
          id: w.id || uuidv4(),
          component_type: this.mapWidgetTypeToComponent(w.type),
        })),
      });
      
      if (dashboard.id) {
        // Navigate to the new dashboard
        setTimeout(() => {
          window.location.href = `/analytics/dashboard/${dashboard.id}`;
        }, 500);
      }
      
      return {
        success: true,
        dashboardId: dashboard.id,
      };
    } catch (error) {
      console.error('Dashboard creation error:', error);
      return {
        success: false,
        error: 'Failed to create dashboard',
      };
    }
  }

  /**
   * Modify existing dashboard
   */
  async modifyDashboard(action: ModifyDashboardAction): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { dashboard_id, modification, entities } = action.params;
      
      // Get existing dashboard
      const dashboard = await this.dashboardService.getDashboard(dashboard_id);
      
      switch (modification) {
        case 'add_widget':
          return this.addWidget(dashboard, entities);
          
        case 'remove_widget':
          return this.removeWidget(dashboard, entities);
          
        case 'update_filters':
          return this.updateFilters(dashboard, entities);
          
        default:
          return {
            success: false,
            error: `Unknown modification: ${modification}`,
          };
      }
    } catch (error) {
      console.error('Dashboard modification error:', error);
      return {
        success: false,
        error: 'Failed to modify dashboard',
      };
    }
  }

  /**
   * Generate Swapy-compatible layout
   */
  private generateSwapyLayout(widgets: DashboardWidget[]): any {
    const slots: any[] = [];
    const items: Record<string, any> = {};
    
    widgets.forEach((widget, index) => {
      const slotId = `slot-${index}`;
      const itemId = widget.id || `widget-${index}`;
      
      // Create slot
      slots.push({
        id: slotId,
        position: widget.position,
        size: widget.size,
      });
      
      // Create item
      items[itemId] = {
        slotId,
        component: this.mapWidgetTypeToComponent(widget.type),
        props: {
          title: widget.title,
          metricType: widget.metric_type,
          config: widget.config,
        },
      };
    });
    
    return {
      slots,
      items,
      version: '1.0',
    };
  }

  /**
   * Map widget types to component names
   */
  private mapWidgetTypeToComponent(widgetType: string): string {
    const componentMap: Record<string, string> = {
      line_chart: 'LineChartWidget',
      bar_chart: 'BarChartWidget',
      pie_chart: 'PieChartWidget',
      number_card: 'MetricCardWidget',
      table: 'DataTableWidget',
      calendar: 'CalendarWidget',
    };
    
    return componentMap[widgetType] || 'UnknownWidget';
  }

  /**
   * Add widget to dashboard
   */
  private async addWidget(
    dashboard: any,
    entities: any
  ): Promise<{ success: boolean; error?: string }> {
    const newWidget: DashboardWidget = {
      type: entities.widget_type || 'line_chart',
      metric_type: entities.metric || 'attendance',
      title: entities.title || 'New Widget',
      position: this.findEmptyPosition(dashboard.widgets),
      size: { width: 6, height: 4 },
      config: {
        time_range: entities.time_range,
        filters: entities.filters || {},
      },
    };
    
    const updatedWidgets = [...dashboard.widgets, newWidget];
    
    await this.dashboardService.updateDashboard(dashboard.id, {
      widgets: updatedWidgets,
      config: {
        ...dashboard.config,
        layout: this.generateSwapyLayout(updatedWidgets),
      },
    });
    
    return { success: true };
  }

  /**
   * Find empty position for new widget
   */
  private findEmptyPosition(existingWidgets: any[]): { x: number; y: number } {
    if (!existingWidgets || existingWidgets.length === 0) {
      return { x: 0, y: 0 };
    }
    
    // Find the bottom-most widget
    let maxY = 0;
    existingWidgets.forEach(widget => {
      const bottomY = widget.position.y + widget.size.height;
      if (bottomY > maxY) maxY = bottomY;
    });
    
    return { x: 0, y: maxY };
  }

  /**
   * Remove widget from dashboard
   */
  private async removeWidget(
    dashboard: any,
    entities: any
  ): Promise<{ success: boolean; error?: string }> {
    const widgetId = entities.widget_id;
    if (!widgetId) {
      return {
        success: false,
        error: 'No widget ID specified',
      };
    }
    
    const updatedWidgets = dashboard.widgets.filter(
      (w: any) => w.id !== widgetId
    );
    
    await this.dashboardService.updateDashboard(dashboard.id, {
      widgets: updatedWidgets,
      config: {
        ...dashboard.config,
        layout: this.generateSwapyLayout(updatedWidgets),
      },
    });
    
    return { success: true };
  }

  /**
   * Update dashboard filters
   */
  private async updateFilters(
    dashboard: any,
    entities: any
  ): Promise<{ success: boolean; error?: string }> {
    const filters = entities.filters || {};
    
    // Update all widgets with new filters
    const updatedWidgets = dashboard.widgets.map((widget: any) => ({
      ...widget,
      config: {
        ...widget.config,
        filters: {
          ...widget.config.filters,
          ...filters,
        },
      },
    }));
    
    await this.dashboardService.updateDashboard(dashboard.id, {
      widgets: updatedWidgets,
    });
    
    return { success: true };
  }

  /**
   * Validate dashboard configuration
   */
  static validateDashboard(params: any): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    if (!params.title) {
      errors.push('Dashboard title is required');
    }
    
    if (!params.widgets || params.widgets.length === 0) {
      errors.push('Dashboard must have at least one widget');
    }
    
    params.widgets?.forEach((widget: any, index: number) => {
      if (!widget.type) {
        errors.push(`Widget ${index + 1}: type is required`);
      }
      if (!widget.metric_type) {
        errors.push(`Widget ${index + 1}: metric type is required`);
      }
    });
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }
}